import Cookies from 'js-cookie'
import {
  useContext,
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  ReactNode,
  useMemo,
} from 'react'
import { AuthService } from '@/lib/services/auth'
import { Permission, Role, User } from '../types'
import { instance } from '@/lib/http/axios'
import { LoadingSpinner } from '@/components/layout/loading-spinner'
import { useRouter } from 'next/router'
import { keyBy } from '@/lib/utils'
import analyticsService from '@/lib/services/analytics'
import { EventName } from '@/types/analytics'

export interface LoginParams {
  email: string
  password: string
  redirectUrl?: string
}

interface AuthContextProps {
  authenticated: boolean
  user: User | null
  setUser: Dispatch<SetStateAction<User | null>>
  logout: () => void
  login: (params: LoginParams) => Promise<void>
  isLoading: boolean
  loginSuccessCallback: (token: string) => void
  hasPermission: (permission: Permission) => boolean
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps
)

export const REDIRECT_PAGE = 'REDIRECT_PAGE'

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const router = useRouter()

  const initlizeUser = async (token: string) => {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`

    try {
      const response = await AuthService.me()
      setUser(response)

      Cookies.set('permissions', (response?.permissions ?? []).join('||'))

      // Identify user in Mixpanel
      if (response) {
        analyticsService.identify(response.id.toString(), {
          id: response.id.toString(),
          email: response.email,
          name: `${response.firstName} ${response.lastName}`,
          companyId: response.companyId?.toString(),
          companyName: response.company?.name,
          role: response.role,
          permissions: response.permissions,
        })
      }

      return response
    } catch (e) {
      Cookies.remove('token')
      Cookies.remove('permissions')

      window.location.reload()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const token = JSON.parse(Cookies.get('token') ?? 'null')
    if (token) {
      initlizeUser(token)
    } else {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (router.query.clearCache) {
      Cookies.remove('token')
      Cookies.remove('permissions')
      window.location.href = 'https://app.aloesuite.com'
    }
  }, [router])

  const loginSuccessCallback = async (token: string) => {
    const user = await initlizeUser(token)
    Cookies.set('token', JSON.stringify(token))

    // Track login event
    if (user) {
      analyticsService.track(EventName.Login, {
        userId: user.id,
        userEmail: user.email,
        userRole: user.role,
        companyId: user.companyId,
        method: 'email',
      })
    }

    router.push(
      Cookies.get(REDIRECT_PAGE) ??
        (user?.role === Role.Staff ? '/bookings' : '/insights')
    )

    Cookies.remove(REDIRECT_PAGE)
  }

  const permissionByKey = useMemo(
    () => keyBy(user?.permissions),
    [user?.permissions]
  )

  const hasPermission = (permission: Permission) => {
    if (!user) {
      return false
    }
    return !!permissionByKey[permission]
  }

  const login = async ({ email, password }: LoginParams) => {
    const response = await AuthService.login({ email, password })
    await loginSuccessCallback(response.token)
  }

  const logout = () => {
    // Track logout event before resetting
    if (user) {
      analyticsService.track(EventName.Logout, {
        userId: user.id,
        userEmail: user.email,
      })
    }

    // Reset Mixpanel
    analyticsService.reset()

    Cookies.remove('token')
    Cookies.remove('permissions')
    setUser(null)
    AuthService.logout()

    Cookies.remove(REDIRECT_PAGE)
    router.push('/auth/login')
  }

  if (router.pathname.startsWith('/app')) {
    return <div>{children}</div>
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <AuthContext.Provider
      value={{
        authenticated: !isLoading && !!user,
        user,
        setUser,
        logout,
        login,
        isLoading,
        loginSuccessCallback,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export default useAuth
